/**
 * THE STOP SHEET, which no artboard draws (business issue 13), so it is built from drawn parts:
 * Schedule's sheet (`Sheet.tsx`), the artboards' buttons, and the sign-in screen's code step
 * (`FreshCode.tsx`). The seam's "Stop" beside "Charges automatically: Visa ending 4242, since 12
 * Oct" on Payments opens it (ops's `docs/seams/home.md`, l.197-199 at b727ec1). The words are in
 * `words.ts`.
 *
 * - IT ASKS FIRST: "Stop charging Visa ending 4242 automatically?", the method in ops's words for it
 *   (`savedMethodWords`, the seam, section 5 at 0b51e49), with "Yes, stop it" and "Keep it".
 * - WHAT IS SENT is the seam's Stop, `billing.autopay_stop`, naming the household and nothing else,
 *   since a household has at most one method (l.540). It goes to the door a fresh code stands in
 *   front of, `POST /api/requests/billing` (CLAUDE.md binding rule 5).
 * - A FRESH CODE FIRST, when the door asks for one (`403 step_up_required`), which it does unless a
 *   code was typed in this session in the last fifteen minutes: the sheet emails one to the address
 *   she signed in with, or texts it to her number where she signed in by it (home #11), and says
 *   why, and once she has typed it sends the same Stop again with the same key, so the door writes
 *   one row. A Stop that then does not go through is sent again without the code, which has worked
 *   (`FreshCode.tsx`).
 * - ONE STOP HOWEVER OFTEN IT IS TAPPED, AND HOWEVER OFTEN THE SHEET IS CLOSED. Whether one is out,
 *   and its key, are held by Payments (`lib/asks.ts`), never by the sheet, so a sheet closed while
 *   one is out loses nothing, and one opened again finds it still out or reuses its key. The key is
 *   let go once the door has written the Stop, or said nothing is saved.
 * - ASKING IS ALL IT DOES (CLAUDE.md binding rule 1). Sent is Payments' to say, as "Sent to the
 *   office."; the method is said to be stopped only once ops's receipt says so, or its push that
 *   nothing is saved comes after it (`screens/Payments.tsx`).
 * - WHAT THE DOOR ANSWERS decides what follows, whether or not the sheet is still open: sent and
 *   nothing saved are Payments' to say, and close the sheet; a 401 is the way to the door; a 404,
 *   the household gone or not hers to ask about now, which the door answers alike, says it cannot
 *   be stopped here, with the phone, in the same words either way, on the question, from the code
 *   step too, takes the focus and reads Payments again, and the sheet stays open on it even where
 *   Payments read again holds no method (`SavedMethod.tsx` keeps it drawn), never closing in
 *   silence (the owner, 2026-10-20); Stripe off at ops says it cannot be stopped here, with the
 *   phone; anything else says it did not go through, with the phone, and leaves the sheet to try
 *   again. What the sheet says, it says only while it is open.
 */
import { useEffect, useRef, useState } from 'react';
import { sendRequest, type SignedInWith, type TooMany } from '../api.ts';
import { keyFor, type Asking } from '../lib/asks.ts';
import { noFreshCode } from '../lib/fresh.ts';
import { FAILED_BEFORE_PHONE, STOP, stopCharging, tooManyBeforePhone } from '../words.ts';
import { GHOST_BUTTON, NAVY_BUTTON } from './buttons.ts';
import { FreshCode, type ProvedCame } from './FreshCode.tsx';
import { Phone } from './Phone.tsx';
import { Sheet } from './Sheet.tsx';

/** The seam's Stop. */
const STOP_TYPE = 'billing.autopay_stop';

/** What the Stop asked for comes to, as the sheet acts on it. */
type Came = ProvedCame;

/**
 * A line the sheet says on its first step, and the count of lines said so far; for the door's own
 * limits, whose count and when, as the door said them.
 */
interface Line {
  /** `not_here` is the door's 404: the Stop no longer one she may ask for here. */
  readonly said: 'failed' | 'closed' | 'too_many' | 'not_here';
  readonly count: number;
  readonly limit?: TooMany;
}

interface StopSheetProps {
  /** Stripe's words for the method, as ops gives them, which ops's words for it are made from. */
  readonly label: string | undefined;
  /** Ops's type of the method, `card`, `us_bank_account` or `link`, absent for one it does not know. */
  readonly methodType: string | undefined;
  /** The address or mobile number she signed in with, which a fresh code goes to (home #11). */
  readonly signedInWith: SignedInWith;
  /** The household Payments shows, which the Stop is about. */
  readonly household: string;
  /** Whether a Stop is out, and its key, held by Payments. */
  readonly asking: Asking;
  /** Closes the sheet, having asked nothing more. */
  readonly onClose: () => void;
  /** The door wrote the Stop, whose id this is. */
  readonly onSent: (id: string) => void;
  /** The door says nothing is saved to stop: what Payments shows is out of date. */
  readonly onNothingSaved: () => void;
  /** The session has ended. */
  readonly onSignedOut: () => void;
  /** What Payments shows may be out of date: read it again. */
  readonly onStale: () => void;
}

export function StopSheet({
  label,
  methodType,
  signedInWith,
  household,
  asking,
  onClose,
  onSent,
  onNothingSaved,
  onSignedOut,
  onStale,
}: StopSheetProps) {
  const [step, setStep] = useState<'ask' | 'code'>('ask');
  const [line, setLine] = useState<Line | null>(null);
  const [busy, setBusy] = useState(false);
  const lines = useRef(0);
  /** What the sheet says of the door's 404, which takes the focus. */
  const notHereLine = useRef<HTMLParagraphElement>(null);
  /** Whether this sheet is still open, and so has anything to say. */
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // The Stop is no longer one she may ask for here: what the sheet says of it takes the focus, so
  // it is read out and stays in the sheet, where Escape still closes it, even where Payments read
  // again takes Stop away.
  useEffect(() => {
    if (line?.said === 'not_here') notHereLine.current?.focus();
  }, [line]);

  /** Says a line on the question, drawn afresh each time, so the same line said twice is heard. */
  const say = (said: Line['said'], limit?: TooMany) => {
    lines.current += 1;
    setLine(
      limit === undefined ? { said, count: lines.current } : { said, count: lines.current, limit },
    );
  };

  /** Sends the Stop, with the key Payments holds for it, and acts on what the door answers. */
  async function stop(): Promise<Came> {
    asking.out = true;
    const answer = await sendRequest(
      { key: keyFor(asking, STOP_TYPE), type: STOP_TYPE, household, payload: {} },
      'billing',
    );
    asking.out = false;
    switch (answer.outcome) {
      case 'sent':
        asking.keys.clear();
        onSent(answer.id);
        return null;
      case 'step_up':
        // The door decides: no fresh code stands, whatever the tab knew of one (`lib/fresh.ts`).
        noFreshCode();
        return 'step_up';
      case 'signed_out':
        onSignedOut();
        return null;
      // The household is gone, or not hers to ask about now, which the door answers alike (binding
      // rule 2): said on the question, in the same words either way, never silent, and Payments
      // read again; from the code step too, which has nothing more to do.
      case 'not_found':
        onStale();
        if (mounted.current) {
          setStep('ask');
          say('not_here');
        }
        return null;
      case 'refused':
        if (answer.code === 'nothing_saved') {
          asking.keys.clear();
          onNothingSaved();
          return null;
        }
        return answer.code === 'payments_off' ? 'closed' : 'failed';
      case 'too_many':
        return { tooMany: answer.limit };
      default:
        return 'failed';
    }
  }

  async function yes() {
    if (asking.out) return;
    setBusy(true);
    setLine(null);
    const came = await stop();
    if (!mounted.current) return;
    setBusy(false);
    if (came === 'step_up') {
      setStep('code');
    } else if (came !== null) {
      if (typeof came === 'object') say('too_many', came.tooMany);
      else say(came);
    }
  }

  /**
   * Once the fresh code works: the same Stop again, and what to say if it does not go through, or
   * that the door asks for a code again, once the fifteen minutes of the one she typed are over.
   */
  async function proved(): Promise<ProvedCame> {
    if (asking.out) return null;
    return stop();
  }

  return (
    <Sheet heading={stopCharging(label, methodType)} onClose={onClose}>
      {step === 'code' ? (
        <FreshCode
          signedInWith={signedInWith}
          why={STOP.freshCode}
          submit={STOP.yes}
          closedBeforePhone={STOP.cannotBeforePhone}
          onProved={proved}
          className="mt-4"
        />
      ) : (
        <>
          <div aria-live="polite">
            {line === null ? null : (
              <p
                key={line.count}
                ref={line.said === 'not_here' ? notHereLine : undefined}
                tabIndex={line.said === 'not_here' ? -1 : undefined}
                className="mt-4 text-body text-pretty text-text-body"
              >
                {line.said === 'closed' || line.said === 'not_here'
                  ? STOP.cannotBeforePhone
                  : line.said === 'too_many'
                    ? tooManyBeforePhone(line.limit)
                    : FAILED_BEFORE_PHONE}{' '}
                <Phone after="." />
              </p>
            )}
          </div>
          <button
            type="button"
            aria-disabled={busy ? true : undefined}
            onClick={() => void yes()}
            className={`mt-6 cursor-pointer aria-disabled:cursor-wait aria-disabled:opacity-70 ${NAVY_BUTTON}`}
          >
            {STOP.yes}
          </button>
        </>
      )}
      <button type="button" onClick={onClose} className={`mt-3 cursor-pointer ${GHOST_BUTTON}`}>
        {STOP.keep}
      </button>
    </Sheet>
  );
}
